import { useEffect, useRef, useState } from 'react';

const prefersStill = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches || Boolean(navigator.connection?.saveData);

export const useMoleculePlayback = () => {
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const [format, setFormat] = useState(() => {
    const size = window.matchMedia('(max-width: 760px)').matches ? 'mobile' : 'desktop';
    const h264 = document.createElement('video').canPlayType('video/mp4; codecs="avc1.640020"');
    return { size, extension: h264 ? 'mp4' : 'webm' };
  });
  const asset = `/media/betaris-hero-quality-${format.size}.${format.extension}`;
  const poster = `/media/hero-quality-poster-${format.size}.webp`;
  const [source, setSource] = useState(null);
  const [wanted, setWanted] = useState(() => !prefersStill());
  const [resumeCounter, setResumeCounter] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState(false);
  const canRun = wanted;
  const allowed = useRef(canRun);
  allowed.current = canRun;

  useEffect(() => {
    const breakpoint = window.matchMedia('(max-width: 760px)');
    const update = event => {
      videoRef.current?.pause();
      setFormat(previous => ({ ...previous, size: event.matches ? 'mobile' : 'desktop' }));
      setSource(null);
      setHasPlayed(false);
      setPlaying(false);
      setError(false);
      setBlocked(false);
    };
    breakpoint.addEventListener('change', update);
    return () => breakpoint.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    // Keep the molecular scene running independently of scrolling. If a browser
    // suspends background media itself, request playback again on tab return.
    const visibility = () => { if (!document.hidden) setResumeCounter(previous => previous + 1); };
    const preference = event => { if (event.matches) setWanted(false); };
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    document.addEventListener('visibilitychange', visibility);
    media.addEventListener('change', preference);
    return () => { document.removeEventListener('visibilitychange', visibility); media.removeEventListener('change', preference); };
  }, []);

  useEffect(() => { if (canRun && !source) setSource(asset); }, [canRun, source, asset]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    if (!canRun || !source) { video.pause(); setPlaying(false); }
    else {
      video.muted = true;
      video.play()?.catch(reason => {
        if (!cancelled && reason.name !== 'AbortError') {
          setBlocked(reason.name === 'NotAllowedError');
          if (reason.name !== 'NotAllowedError') setError(true);
          setWanted(false); setPlaying(false);
        }
      });
    }
    return () => { cancelled = true; };
  }, [canRun, source, resumeCounter]);
  useEffect(() => { const video = videoRef.current; return () => video?.pause(); }, []);

  const start = () => {
    const video = videoRef.current;
    if (!video) return;
    setBlocked(false); setError(false); setWanted(true);
    if (!source) { video.src = asset; setSource(asset); }
    if (error) video.load();
    video.muted = true;
    video.play()?.catch(reason => { if (reason.name !== 'AbortError') { setBlocked(true); setWanted(false); } });
  };
  const onPlaying = () => {
    if (!allowed.current) { videoRef.current?.pause(); return; }
    setPlaying(true); setHasPlayed(true); setBlocked(false); setError(false);
  };
  const onError = () => { setError(true); setWanted(false); setPlaying(false); setHasPlayed(false); };
  return { videoRef, stageRef, source, poster, canRun, wanted, playing, hasPlayed, blocked, error, start, onPlaying, onPause: () => setPlaying(false), onError };
};